/**
 * Splits scheduled organizer messages into the ones that fall before the
 * trip's first day and the ones that fall after its last.
 *
 * Why this exists as its own tested function rather than inline in the
 * mixin. The schedulable window is wider than the trip -- a week either
 * side since 2026-10-05 -- so a message can legitimately carry a date that
 * no trip day represents. Those messages are still scheduled and still
 * BILLED per traveller, and neither view has a day row to hang them on: the
 * list view iterates attractionsByDay, and the calendar renders only the
 * months the trip spans (tripMonths + filterEventsForMonth +
 * showNonCurrentDates: false), so a date in an adjacent month has no cell
 * at all. A message that lands in no bucket is invisible, un-editable and
 * un-deletable while still going out.
 *
 * Hence the invariant this function is tested against: every message handed
 * in comes back in exactly one bucket. Never dropped, never duplicated.
 * `onTrip` is returned for that accounting -- the views get their per-day
 * rows from messagesForDayIndex, which is keyed by day index rather than by
 * date -- and it is what lets a test assert the three buckets sum to the
 * input.
 */

// Date and time are zero-padded fixed-width ISO-ish strings
// ('2026-10-05', '08:30'), so lexicographic order IS chronological order
// and no Date parsing is needed.
function byDateThenTime( a, b ) {
  return `${a.target_date || ''}${a.send_time || ''}`
    .localeCompare(`${b.target_date || ''}${b.send_time || ''}`);
}

/**
 * @param {Array} messages    scheduled messages, each with target_date
 *                            ('YYYY-MM-DD') and send_time ('HH:mm')
 * @param {Array} tripDates   the trip's day dates as 'YYYY-MM-DD', any order
 * @returns {{before: Array, onTrip: Array, after: Array}} each sorted
 */
function partitionOrganizerMessages( messages, tripDates ) {
  const list = Array.isArray(messages) ? messages : [];
  const dates = (Array.isArray(tripDates) ? tripDates : []).filter(Boolean);
  const tripDateSet = new Set(dates);

  // Not dates[0]: the caller derives these from a day index, and sorting
  // here means a caller that hands them over unordered still gets a
  // correct split rather than a silently wrong one.
  const firstTripDate = dates.length
    ? dates.slice().sort()[0]
    : null;

  const before = [];
  const onTrip = [];
  const after = [];

  list.forEach(( message ) => {
    if ( tripDateSet.has(message.target_date) ) {
      onTrip.push(message);
      return;
    }
    // No trip days (a ride with no start_date, or attractionsByDay not
    // loaded yet) means there is nothing to be "before", so everything
    // outside goes to one bucket. Showing a message under the wrong
    // heading is recoverable; hiding it is not.
    //
    // A message with a missing or malformed target_date also lands here and
    // sorts first, which is deliberate: it stays visible and deletable.
    if ( firstTripDate && String(message.target_date || '') < firstTripDate ) {
      before.push(message);
    } else {
      after.push(message);
    }
  });

  return {
    before: before.sort(byDateThenTime),
    onTrip: onTrip.sort(byDateThenTime),
    after: after.sort(byDateThenTime)
  };
}

// CommonJS, matching src/utils/organizerMessagePayload.js: webpack consumes
// it from the components, and tests/unit can require it under plain node
// with no transpile step.
module.exports = { partitionOrganizerMessages };
